import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarClock, Check, Crown, Send } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { useLocalized } from '@/hooks/useLocalized';
import { formatDate, formatSom } from '@/lib/format';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/store/auth';
import { useSeo } from '@/hooks/useSeo';

const PREMIUM_MONTHLY = 29000;
const PREMIUM_YEARLY = 290000; // ~2 oy bepul
/** Yillikda chizib ko'rsatiladigan "chegirmasiz" narx — 12 oy oylik tarif bo'yicha */
const PREMIUM_YEARLY_FULL = PREMIUM_MONTHLY * 12;
const YEARLY_DISCOUNT_PCT = Math.round((1 - PREMIUM_YEARLY / PREMIUM_YEARLY_FULL) * 100);
/** Yillik Premium'ni chegirma narxida bo'lib to'lash — admin bilan kelishiladi */
const YEARLY_INSTALLMENTS = 6;
const YEARLY_INSTALLMENT = Math.round(PREMIUM_YEARLY / YEARLY_INSTALLMENTS);
// To'lov tizimi hali yo'q — premium admin bilan Telegram orqali kelishib, qo'lda beriladi.
const PREMIUM_CONTACT_URL =
  import.meta.env.VITE_PREMIUM_CONTACT_URL || 'https://t.me/fast_support_admin';

export function Pricing() {
  const { t } = useTranslation();
  const { lang } = useLocalized();
  const [yearly, setYearly] = useState(false);
  useSeo({
    title: 'Premium tariflar | SQLMaster.uz',
    description:
      "SQLMaster.uz tariflari: bepul boshlang yoki Premium bilan premium SQL masalalarga kirish oling. Bepul va Premium imkoniyatlarini solishtiring.",
    path: '/pricing',
  });
  const { user, isAuthenticated } = useAuthStore();

  const freeFeatures = t('pricing.freeFeatures', { returnObjects: true }) as string[];
  const premiumFeatures = t('pricing.premiumFeatures', { returnObjects: true }) as string[];
  const faq = t('pricing.faq', { returnObjects: true }) as Array<{ q: string; a: string }>;

  const premiumPrice = yearly ? PREMIUM_YEARLY : PREMIUM_MONTHLY;
  const isPremium = !!user?.isPremium;
  const expiresAt = user?.premiumExpiresAt;
  const daysLeft = expiresAt
    ? Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86_400_000))
    : null;
  const expiringSoon = daysLeft !== null && daysLeft <= 7;

  // Admin lichkasida tayyor turadigan xabar (t.me/<username>?text=...)
  const premiumContactHref = `${PREMIUM_CONTACT_URL}?text=${encodeURIComponent(
    t(isPremium ? 'pricing.premiumRenewMessage' : 'pricing.premiumContactMessage', {
      plan: yearly ? t('pricing.yearly') : t('pricing.monthly'),
      price: formatSom(premiumPrice, lang),
      account: user ? `${user.email} (@${user.username})` : '',
      interpolation: { escapeValue: false },
    }),
  )}`;

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold sm:text-4xl">{t('pricing.title')}</h1>
        <p className="mx-auto mt-3 max-w-2xl text-lg text-content-secondary">{t('pricing.subtitle')}</p>
      </div>

      {/* Joriy tarif */}
      {isAuthenticated && (
        <Card
          className={cn(
            'mx-auto mb-10 flex max-w-2xl items-center gap-4 p-4 sm:p-5',
            isPremium && (expiringSoon ? 'border-[var(--medium)]/50' : 'border-brand-500/40'),
          )}
        >
          <div
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
              isPremium ? 'bg-[var(--medium)]/15 text-[var(--medium)]' : 'bg-surface text-content-muted',
            )}
          >
            <Crown className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-content-muted">{t('pricing.currentPlan')}</p>
            <p className="font-semibold">
              {isPremium ? t('pricing.premiumPlan') : `${t('pricing.freePlan')} (${t('pricing.free')})`}
            </p>
            {isPremium && expiresAt && (
              <p className={cn('mt-0.5 text-sm', expiringSoon ? 'text-[var(--medium)]' : 'text-content-secondary')}>
                {t('pricing.activeUntil', { date: formatDate(expiresAt, lang) })}
                {' · '}
                {t('pricing.daysLeft', { count: daysLeft ?? 0 })}
              </p>
            )}
          </div>
        </Card>
      )}

      {/* Toggle */}
      <div className="mb-10 flex items-center justify-center gap-3">
        <span className={cn('text-sm', !yearly ? 'font-semibold text-content' : 'text-content-muted')}>
          {t('pricing.monthly')}
        </span>
        <button
          onClick={() => setYearly((v) => !v)}
          className="relative h-6 w-11 rounded-full bg-surface-hover transition-colors"
          role="switch"
          aria-checked={yearly}
          aria-label={t('pricing.yearly')}
        >
          <span
            className={cn(
              'absolute top-0.5 h-5 w-5 rounded-full bg-[var(--brand-primary)] transition-transform',
              yearly ? 'left-0.5 translate-x-5' : 'left-0.5',
            )}
          />
        </button>
        <span className={cn('text-sm', yearly ? 'font-semibold text-content' : 'text-content-muted')}>
          {t('pricing.yearly')}
        </span>
        <span className="rounded-full bg-[var(--easy)]/15 px-2 py-0.5 text-xs font-semibold text-[var(--easy)]">
          {t('pricing.yearlySave')}
        </span>
      </div>

      {/* Plans */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Free */}
        <Card className="relative flex flex-col p-6 sm:p-8">
          {isAuthenticated && !isPremium && <CurrentBadge label={t('pricing.currentBadge')} />}
          <h2 className="text-lg font-semibold">{t('pricing.freePlan')}</h2>
          <div className="mt-3 flex items-baseline gap-1">
            <span className="text-4xl font-bold">{t('pricing.free')}</span>
          </div>
          <p className="mt-1 text-sm text-content-muted">&nbsp;</p>
          <ul className="my-6 flex-1 space-y-3">
            {freeFeatures.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--easy)]" />
                <span className="text-content-secondary">{f}</span>
              </li>
            ))}
          </ul>
          <Link to={isAuthenticated ? '/problems' : '/register'} className="btn btn-secondary w-full py-2.5">
            {isAuthenticated ? t('pricing.freeCtaAuthed') : t('pricing.freeCta')}
          </Link>
        </Card>

        {/* Premium */}
        <Card className="relative flex flex-col border-brand-500/40 p-6 shadow-glow sm:p-8">
          {isPremium ? (
            <CurrentBadge label={t('pricing.currentBadge')} />
          ) : (
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[var(--brand-primary)] px-3 py-1 text-xs font-semibold text-white">
              {t('pricing.popular')}
            </span>
          )}
          <h2 className="text-lg font-semibold">{t('pricing.premiumPlan')}</h2>
          {yearly && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-lg font-medium text-content-muted line-through decoration-2">
                {formatSom(PREMIUM_YEARLY_FULL, lang)}
              </span>
              <span className="rounded-full bg-[var(--easy)]/15 px-2 py-0.5 text-xs font-semibold text-[var(--easy)]">
                −{YEARLY_DISCOUNT_PCT}%
              </span>
            </div>
          )}
          <div className={cn('flex items-baseline gap-1', yearly ? 'mt-1' : 'mt-3')}>
            <span className="text-4xl font-bold">{formatSom(premiumPrice, lang)}</span>
          </div>
          <p className="mt-1 text-sm text-content-muted">
            {yearly ? t('pricing.perYear') : t('pricing.perMonth')}
          </p>
          {yearly && (
            <div className="mt-4 flex items-start gap-2.5 rounded-lg bg-brand-500/10 px-3 py-2.5 text-sm">
              <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-brand-600 dark:text-brand-400" aria-hidden />
              <p className="text-content-secondary">
                <span className="font-semibold text-content">
                  {t('pricing.installmentTitle', { amount: formatSom(YEARLY_INSTALLMENT, lang) })}
                </span>{' '}
                {t('pricing.installmentNote')}
              </p>
            </div>
          )}
          <ul className="my-6 flex-1 space-y-3">
            {premiumFeatures.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--brand-secondary)]" />
                <span className="text-content-secondary">{f}</span>
              </li>
            ))}
          </ul>
          {isAuthenticated ? (
            <a
              href={premiumContactHref}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary w-full gap-2 py-2.5"
            >
              <Send className="h-4 w-4" />
              {isPremium ? t('pricing.renewCta') : t('pricing.premiumCta')}
            </a>
          ) : (
            <Link to="/register" className="btn btn-primary w-full py-2.5">
              {t('pricing.premiumCta')}
            </Link>
          )}
        </Card>
      </div>

      {/* FAQ */}
      <div className="mx-auto mt-16 max-w-2xl">
        <h2 className="mb-6 text-center text-2xl font-bold">{t('pricing.faqTitle')}</h2>
        <div className="space-y-3">
          {faq.map((item, i) => (
            <details key={i} className="group rounded-lg border border-line bg-elevated p-4">
              <summary className="flex cursor-pointer list-none items-center justify-between font-medium">
                {item.q}
                <span className="text-content-muted transition-transform group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-content-secondary">{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
}

function CurrentBadge({ label }: { label: string }) {
  return (
    <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[var(--easy)] px-3 py-1 text-xs font-semibold text-white">
      {label}
    </span>
  );
}
